import { themeAtom } from "../../../shared/atoms";
import { ExcelLikeGrid } from "../../../shared/excel-grid/components/ExcelLikeGrid";
import type { AdminCrewReportUploadModalProps } from "../../models/admin-crew-report.models";
import { applyAdminCrewReportUploadCellCommit } from "../../utils/admin-crew-report.utils";
import AdminCrewReportUploadModalActions from "./AdminCrewReportUploadModalActions";
import AdminCrewReportUploadModalHeader from "./AdminCrewReportUploadModalHeader";
import { Dialog } from "@headlessui/react";
import { useAtomValue } from "jotai";
import { useCallback, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

const AdminCrewReportUploadModal = ({
  preview,
  fileVesselImo,
  vesselDetailsStatus,
  vesselDetails,
  isSaving,
  submitDisabledReason,
  submitMessage,
  submitMessageDetails,
  submitMessageTone,
  onRowsChange,
  onCancel,
  onSubmit,
}: AdminCrewReportUploadModalProps) => {
  const theme = useAtomValue(themeAtom);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const [dialogPanelElement, setDialogPanelElement] = useState<HTMLElement | null>(null);
  const handleDialogPanelRef = useCallback((element: HTMLElement | null) => {
    setDialogPanelElement(element);
  }, []);

  const handleClose = () => {
    if (isSaving) {
      return;
    }

    onCancel();
  };

  return (
    <Dialog
      as="div"
      open
      initialFocus={titleRef}
      onClose={handleClose}
      data-test="admin-crew-report-upload-modal"
      className="fixed inset-0 z-50"
    >
      <div className="fixed inset-0 bg-black/45" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center px-3 py-4">
        <Dialog.Panel
          as="section"
          ref={handleDialogPanelRef}
          className={twMerge(
            "flex max-h-[92vh] w-full max-w-[min(1480px,96vw)] flex-col bg-white shadow-2xl dark:bg-gray-800",
            theme.home.tailwind.textDefault,
          )}
        >
          <AdminCrewReportUploadModalHeader
            preview={preview}
            fileVesselImo={fileVesselImo}
            vesselDetailsStatus={vesselDetailsStatus}
            vesselDetails={vesselDetails}
            isSaving={isSaving}
            titleRef={titleRef}
            onCancel={onCancel}
          />

          <div className="flex min-h-0 flex-1 flex-col px-5 py-4">
            <ExcelLikeGrid
              dataTest="admin-crew-report-upload-grid"
              rows={preview.rows}
              columns={preview.columns}
              emptyLabel="No crew rows to preview."
              rowHeight={28}
              cellEditorMode="singleLine"
              virtualized
              virtualizedMaxHeight={560}
              editingDisabled={isSaving}
              useTabForCellNavigation={false}
              selectMenuPortalTarget={dialogPanelElement}
              className="max-h-[calc(92vh-250px)]"
              onCellCommit={(commit) => {
                if (isSaving) {
                  return;
                }

                onRowsChange(applyAdminCrewReportUploadCellCommit(preview.rows, commit));
              }}
            />
          </div>

          <AdminCrewReportUploadModalActions
            disabledReason={submitDisabledReason}
            isSaving={isSaving}
            message={submitMessage}
            messageDetails={submitMessageDetails}
            messageTone={submitMessageTone}
            onCancel={onCancel}
            onSubmit={onSubmit}
          />
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default AdminCrewReportUploadModal;
